import { Component, computed, effect, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ModuleFacade } from '@app/store/module';
import { ModuleItemType } from '@app/store/shared/models';
import { UnavailableComponent } from '@shared/components';
import { DeleteButtonComponent } from './delete-button/delete-button.component';

interface ItemDisplay {
    id: string;
    name: string;
    type: ModuleItemType;
    link: unknown[];
}

const TYPE_PREFIXES: Record<ModuleItemType, string> = {
    lesson: 'lessons',
    quiz: 'quizzes',
    reflection: 'reflections',
};

@Component({
    selector: 'app-module-view-modal',
    templateUrl: './view.component.html',
    styleUrls: ['./view.component.css'],
    imports: [TranslateModule, RouterLink, BadgeModule, ButtonModule, TagModule, DeleteButtonComponent, UnavailableComponent],
})
export class ViewComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly moduleFacade = inject(ModuleFacade);

    readonly module = this.moduleFacade.module;
    readonly loading = this.moduleFacade.loading;
    readonly error = this.moduleFacade.error;
    readonly saving = this.moduleFacade.saving;

    readonly itemsList = computed<ItemDisplay[]>(() => {
        const module = this.module();
        if (!module) return [];

        return module.items.map((item) => {
            const link = ['/', { outlets: { modal: [TYPE_PREFIXES[item.type], 'view', item.id] } }];
            return { id: item.id, name: item.name, type: item.type, link };
        });
    });

    constructor() {
        const id = this.route.parent?.snapshot.params['id'];
        if (id) {
            this.moduleFacade.getModule(id);
        }

        effect(() => {
            if (this.moduleFacade.saved()) {
                this.close({ replaceUrl: !this.moduleFacade.module() });
            }
        });
    }

    ngOnDestroy(): void {
        this.moduleFacade.clear();
    }

    onEdit(): void {
        const module = this.module();
        if (module) {
            this.router.navigate([{ outlets: { modal: ['modules', 'edit', module.id] } }]);
        }
    }

    close({ replaceUrl } = { replaceUrl: false }): void {
        this.router.navigate([{ outlets: { modal: null } }], { replaceUrl });
    }
}
